import React from 'react';
// import PropTypes from 'prop-types';

import {Paper, Stack, Typography, Box, Button} from 'views/components';
import {Translate, translate} from 'views/module';
import {ArrowBack, ArrowForward, Save} from 'views/icons';

import ProgressIndicator from './components/ProgressIndicator';
import SectionSelector from './components/SectionSelector';
import {
  CertificationsSection,
  SkillsAndLanguagesSection,
  WorkHistorySection,
  PersonalitySection,
  WorkEligibilitySection,
  WorkAvailabilitySection,
  TravelPreferencesSection,
  ContactSection,
  ConfirmSection,
  InterviewSection,
} from './sections';

import moment from 'moment';
import {SECTION_KEYS} from './components/constants';
import AgreementsSection from './sections/Agreements';

const CaregiverScreener = () => {
  // Default caregiver data structure
  const defaultCaregiverData = {
    certifications: [],
    skills: [],
    languages: [],
    workHistory: [
      {

        id: '1',
        employerName: '',
        startDate: '',
        endDate: '',
        canContact: false,
      },
    ],
    personality: {},
    availability: {},
    travelPreferences: {
      maxTravelTime: 30, // Default to 30 minutes
    },
    workEligibility: {},
    contact: {},
    agreements: {},
    confirm: {},
  };

  const [caregiverData, setCaregiverData] = React.useState(() => defaultCaregiverData);
  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const [showSaved, setShowSaved] = React.useState(false);
  const [disableNext, setDisableNext] = React.useState(false);

  const caregiverDataRef = React.useRef(null);
  const interviewQuestionsRef = React.useRef([]);

  React.useEffect(() => {
    loadData();
  }, []);

  React.useEffect(() => {
    if (caregiverData.confirm?.submitted) {
      handleSave(-1);
    }
  }, [caregiverData.confirm]);

  // Track validity of each section
  const [sectionValidity, setSectionValidity] = React.useState({
    [SECTION_KEYS.CERTIFICATIONS]: false,
    [SECTION_KEYS.SKILLS_AND_LANGUAGES]: false,
    [SECTION_KEYS.WORK_HISTORY]: false,
    [SECTION_KEYS.PERSONALITY]: false,
    [SECTION_KEYS.AVAILABILITY]: false,
    [SECTION_KEYS.TRAVEL_PREFERENCES]: false,
    [SECTION_KEYS.WORK_ELIGIBILITY]: false,
    [SECTION_KEYS.CONTACT]: false,
    [SECTION_KEYS.AGREEMENTS]: false,
  });

  // Generic handlers that work for any section
  const handleDataChange = React.useCallback((section, data) => {
    setCaregiverData((prevData) => ({...prevData, [section]: data}));
  }, []);

  const handleValidityChange = React.useCallback((section, isValid) => {
    setSectionValidity((prev) => ({...prev, [section]: isValid}));
  }, []);

  // Section-specific handlers are now defined inline in the sections array below

  const handleNavigateToSection = React.useCallback((sectionIndex) => {
    // Prevent direct navigation to interview section without confirmation
    if (sectionIndex === 9 && !caregiverData.confirm?.submitted) {
      return;
    }

    if (sectionIndex >= 0 && sectionIndex < 11) { // 10 total sections
      setCurrentSectionIndex(sectionIndex);
    }
  }, [caregiverData.confirm?.submitted]);


  const chooseN = (arr, n) => {
    const newArr = [...arr];
    const out = [];
    while (out.length < n && newArr.length) {
      const randomIndex = Math.floor(Math.random() * newArr.length);
      out.push(newArr[randomIndex]);
      newArr.splice(randomIndex, 1);
    }
    return out;
  };

  const assessPersonality = () => {
    // generate answers
    const modifiers = {
      patience_1: 1,
      patience_2: 1,
      patience_3: -1,
      adaptability_1: 1,
      adaptability_2: -1,
      adaptability_3: 1,
      initiative_1: 1,
      initiative_2: -1,
      empathy_1: 1,
      empathy_2: -1,
      empathy_3: 1,
      communication_1: 1,
      communication_2: -1,
      communication_3: 1,
      resilience_1: 1,
      resilience_2: -1,
      resilience_3: 1,
      resilience_4: 1,
      resilience_5: 1,
      resilience_6: 1,
    };
    const traitBuckets = {
      patience: ['patience_1', 'patience_2', 'patience_3', 'communication_3'],
      adaptability: ['adaptability_1', 'adaptability_2', 'adaptability_3', 'resilience_5'],
      initiative: ['initiative_1', 'initiative_2'],
      empathy: ['empathy_1', 'empathy_2', 'empathy_3', 'resilience_6'],
      communication: ['communication_1', 'communication_2', 'communication_3'],
      resilience: ['resilience_1', 'resilience_2', 'resilience_3', 'resilience_4'],
    };
    const assessmentResults = {};
    for (const [trait, questions] of Object.entries(traitBuckets)) {
      const totalWeight = questions.reduce((sum, question) => sum += Math.abs(modifiers[question]), 0);
      const score = questions.reduce((sum, question) => {
        if (modifiers[question] < 0) {
          const score = 6 - caregiverData.personality[question];
          sum += score * Math.abs(modifiers[question]);
        } else {
          sum += caregiverData.personality[question] * Math.abs(modifiers[question]);
        }

        return sum;
      }, 0);

      const scoreOutOf5 = (score / totalWeight);
      const finalScore = (scoreOutOf5 - 1)/4;
      assessmentResults[trait] = finalScore;
    }

    return assessmentResults;
  };

  const chooseProfile = (results) => {
    const profiles = {
      companion: {
        patience: 1,
        adaptability: 0.75,
        initiative: 0.5,
        empathy: 1,
        communication: 0.5,
        resilience: 0.5,
      },
      organizer: {
        patience: 0.5,
        adaptability: 0.5,
        initiative: 1,
        empathy: 0.5,
        communication: 1,
        resilience: 0.5,
      },
      problemSolver: {
        patience: 1,
        adaptability: 1,
        initiative: 0.5,
        empathy: 0.75,
        communication: 0.5,
        resilience: 0.5,
      },
      calmingPresence: {
        patience: 1,
        adaptability: 0.5,
        initiative: 0,
        empathy: 0.5,
        communication: 0.5,
        resilience: 1,
      },
      goGetter: {
        patience: 0.5,
        adaptability: 1,
        initiative: 1,
        empathy: 0.5,
        communication: 1,
        resilience: 0.5,
      },
    };

    const euclideanDistance = (profile, results) => {
      return Math.sqrt(Object.keys(profile).reduce((sum, trait) => Math.pow(profile[trait] - results[trait], 2) + sum, 0));
    };


    const nearestProfile = Object.entries(profiles).reduce((closest, [name, traits]) => {
      const distance = euclideanDistance(traits, results);
      if (distance < closest.distance) {
        return {name, distance};
      }
      return closest;
    }, {name: '', distance: Infinity});

    return nearestProfile.name;
  };

  const getSkillQuestions = (skill) => {
    const key = skill.charAt(0).toUpperCase() + skill.slice(1);
    return translate(`caregiverScreening.InterviewQuestions.Skills.${key}`);
  };

  const handleSubmit = () => {
    const personalityResults = assessPersonality();
    const personalityProfile = chooseProfile(personalityResults);


    const questions = [
      translate('caregiverScreening.InterviewQuestions.General.NonSkippable'),
      translate('caregiverScreening.InterviewQuestions.General.WhyCaregiver'),
      translate('caregiverScreening.InterviewQuestions.General.OutsideWork'),
      translate('caregiverScreening.InterviewQuestions.General.BestClients'),
      translate('caregiverScreening.InterviewQuestions.General.PersonalGoals'),
      translate('caregiverScreening.InterviewQuestions.General.WhyChooseYou'),
    ];
    const chosenSkills = chooseN(caregiverData.skills, 2);
    const skillQuestions = chosenSkills
        .map(getSkillQuestions)
        .map((questions) => questions.reverse()); // must be in pop() order

    questions.push(...skillQuestions);
    interviewQuestionsRef.current = questions.reverse();
    caregiverDataRef.current = {
      ...caregiverData,
      workHistory: caregiverData.workHistory.map((entry) => {
        if (!entry.endDate) {
          entry.endDate = moment().format('YYYY-MM');
        }
        return entry;
      }),
      personalityAssessment: {
        personalityResults,
        personalityProfile,
        personalityAnswers: Object.fromEntries(
            Object.entries(caregiverData.personality)
                .map(([key, value]) => [translate(`caregiverScreening.Personality.Questions.${key}.Text`), value]),
        ),
      },
      interview: {
        interviewedSkills: chosenSkills,
      },
      metadata: {
        submitted: true,
        submittedAt: new Date().toISOString(),
      },
    };
    localStorage.setItem('submittedCaregiverData', JSON.stringify(caregiverDataRef.current));
    localStorage.setItem('interviewQuestions', JSON.stringify(interviewQuestionsRef.current));

    setCurrentSectionIndex(10);
    handleSave(-1, caregiverDataRef.current);
  };

  const sections = [
    {
      key: SECTION_KEYS.CERTIFICATIONS,
      label: translate('caregiverScreening.Steps.Certification'),
      Component: (
        <CertificationsSection
          key={SECTION_KEYS.CERTIFICATIONS}
          certifications={caregiverData.certifications}
          onCertificationsChange={React.useCallback((data) => handleDataChange('certifications', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.CERTIFICATIONS, isValid), [handleValidityChange])}
          setDisableNext={setDisableNext}
        />
      ),
    },
    {
      key: SECTION_KEYS.SKILLS_AND_LANGUAGES,
      label: translate('caregiverScreening.Steps.SkillsAndLanguages'),
      Component: (
        <SkillsAndLanguagesSection
          key={SECTION_KEYS.SKILLS_AND_LANGUAGES}
          onSkillsChange={React.useCallback((data) => handleDataChange('skills', data), [handleDataChange])}
          skills={caregiverData.skills}
          languages={caregiverData.languages}
          onLanguagesChange={React.useCallback((data) => handleDataChange('languages', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.SKILLS_AND_LANGUAGES, isValid), [handleValidityChange])}
          setDisableNext={setDisableNext}
        />
      ),
    },
    {
      key: SECTION_KEYS.WORK_HISTORY,
      label: translate('caregiverScreening.Steps.WorkHistory'),
      Component: (
        <WorkHistorySection
          key={SECTION_KEYS.WORK_HISTORY}
          workHistory={caregiverData.workHistory}
          onWorkHistoryChange={React.useCallback((data) => handleDataChange('workHistory', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.WORK_HISTORY, isValid), [handleValidityChange])}
        />
      ),
    },
    {
      key: SECTION_KEYS.PERSONALITY,
      label: translate('caregiverScreening.Steps.Personality'),
      Component: (
        <PersonalitySection
          key={SECTION_KEYS.PERSONALITY}
          onAnswersChange={React.useCallback((data) => handleDataChange('personality', data), [handleDataChange])}
          answers={caregiverData.personality}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.PERSONALITY, isValid), [handleValidityChange])}
        />
      ),
    },
    {
      key: SECTION_KEYS.AVAILABILITY,
      label: translate('caregiverScreening.Steps.Availability'),
      Component: (
        <WorkAvailabilitySection
          key={SECTION_KEYS.AVAILABILITY}
          availability={caregiverData.availability}
          onAvailabilityChange={React.useCallback((data) => handleDataChange('availability', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.AVAILABILITY, isValid), [handleValidityChange])}
        />
      ),
    },
    {
      key: SECTION_KEYS.TRAVEL_PREFERENCES,
      label: translate('caregiverScreening.Steps.Travel'),
      Component: (
        <TravelPreferencesSection
          key={SECTION_KEYS.TRAVEL_PREFERENCES}
          travelPreferences={caregiverData.travelPreferences}
          onTravelPreferencesChange={React.useCallback((data) => handleDataChange('travelPreferences', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.TRAVEL_PREFERENCES, isValid), [handleValidityChange])}
        />
      ),
    },
    {
      key: SECTION_KEYS.WORK_ELIGIBILITY,
      label: translate('caregiverScreening.Steps.Eligibility'),
      Component: (
        <WorkEligibilitySection
          key={SECTION_KEYS.WORK_ELIGIBILITY}
          workEligibility={caregiverData.workEligibility}
          onWorkEligibilityChange={React.useCallback((data) => handleDataChange('workEligibility', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.WORK_ELIGIBILITY, isValid), [handleValidityChange])}
        />
      ),
    },
    {
      key: SECTION_KEYS.CONTACT,
      label: translate('caregiverScreening.Steps.Contact'),
      Component: (
        <ContactSection
          key={SECTION_KEYS.CONTACT}
          contactData={caregiverData.contact}
          onContactChange={React.useCallback((data) => handleDataChange('contact', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.CONTACT, isValid), [handleValidityChange])}
        />
      ),
    },
    {
      key: SECTION_KEYS.AGREEMENTS,
      label: translate('caregiverScreening.Steps.Agreements'),
      Component: (
        <AgreementsSection
          key={SECTION_KEYS.AGREEMENTS}
          agreements={caregiverData.agreements}
          onAgreementsChange={React.useCallback((data) => handleDataChange('agreements', data), [handleDataChange])}
          setValidity={React.useCallback((isValid) => handleValidityChange(SECTION_KEYS.AGREEMENTS, isValid), [handleValidityChange])}
          setDisableNext={setDisableNext}
        />
      ),
    },
    {
      key: SECTION_KEYS.CONFIRM,
      label: translate('caregiverScreening.Steps.Confirm'),
      Component: (
        <ConfirmSection
          key={SECTION_KEYS.CONFIRM}
          confirmData={caregiverData.confirm}
          caregiverData={caregiverData}
          sectionValidity={sectionValidity}
          onNavigateToSection={handleNavigateToSection}
          onSubmitAndProceed={handleSubmit}
        />
      ),
    },
    {
      key: SECTION_KEYS.INTERVIEW,
      label: translate('caregiverScreening.Steps.Interview'),
      Component: (
        <InterviewSection
          key={SECTION_KEYS.INTERVIEW}
          caregiverDataRef={caregiverDataRef}
          interviewQuestionsRef={interviewQuestionsRef}
        />
      ),
    },
  ];


  const handleNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      handleSave(currentSectionIndex + 1);
    }
  };

  const handlePreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      handleSave(currentSectionIndex - 1);
    }
  };

  const handleSave = (curSection=null, data=null) => {
    localStorage.setItem('caregiverData', JSON.stringify(data ?? caregiverData));
    localStorage.setItem('sectionValidity', JSON.stringify(sectionValidity));
    localStorage.setItem('resumeSection', curSection ?? currentSectionIndex);

    setShowSaved(true);
    setTimeout(() => {
      setShowSaved(false);
    }, 2000); // Show "Saved" for 2 seconds
  };

  const loadData = () => {
    const submittedData = localStorage.getItem('submittedCaregiverData');
    if (submittedData) { // at interviews section
      caregiverDataRef.current = JSON.parse(submittedData);
      interviewQuestionsRef.current = JSON.parse(localStorage.getItem('interviewQuestions')) || [];
      setCurrentSectionIndex(sections.findIndex((section) => section.key === 'interview'));
      return;
    }

    const data = localStorage.getItem('caregiverData');
    if (data) {
      const jsonParsed = JSON.parse(data);
      setCaregiverData(jsonParsed);
    }

    const validityData = localStorage.getItem('sectionValidity');
    if (validityData) {
      setSectionValidity(JSON.parse(validityData));
    }

    const sectionIndex = localStorage.getItem('resumeSection');
    if (sectionIndex) {
      const idx = parseInt(sectionIndex, 10) >= 0 ? parseInt(sectionIndex, 10) : 8;
      setCurrentSectionIndex(idx);
    }
  };

  return (
    <Stack className="caregiver-screener" sx={{background: 'linear-gradient(180deg,rgba(252, 246, 239, 1) 0%, rgba(255, 255, 255, 1) 100%)'}}>
      <Paper
        square
        sx={{
          paddingBlock: 2,
          paddingInline: {md: 8, xs: 2},
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{width: {xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: 1400}}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap'}}>
            <Stack sx={{
              width: {
                xs: '100%',
                sm: 'auto',
                md: 'auto',
                lg: 'auto',
                xl: 'auto',
              },
              textAlign: {
                xs: 'center',
                sm: 'start',
                md: 'start',
                lg: 'start',
                xl: 'start',
              },
            }} >
              <Typography variant='h4' fontWeight={600}>
                <Translate expression='caregiverScreening.Main.Title'/>
              </Typography>
              <Typography variant='subtitle1' color='text.secondary'>
                <Translate expression='caregiverScreening.Main.Subtitle'/>
              </Typography>
            </Stack>
            <Box sx={{display: {xs: 'none', sm: 'flex', md: 'flex', lg: 'flex', xl: 'flex'}, alignItems: 'center'}}>
              <Button startIcon={<Save />} disableElevation onClick={handleSave} >
                <Translate expression={showSaved ? 'caregiverScreening.Main.Saved' : 'caregiverScreening.Main.Save'}/>
              </Button>
            </Box>
          </Box>
          <ProgressIndicator
            totalSteps={sections.length}
            curStep={currentSectionIndex + 1}
            sections={sections}
          />
          <Box sx={{mt: 2, display: {xs: 'flex', sm: 'none', md: 'none', lg: 'none', xl: 'none'}, alignItems: 'center', justifyContent: 'center'}}>
            <Button startIcon={<Save />} disableElevation onClick={handleSave} >
              <Translate expression={showSaved ? 'caregiverScreening.Main.Saved' : 'caregiverScreening.Main.Save'}/>
            </Button>
          </Box>
        </Box>
      </Paper>
      <Box
        sx={{
          my: 4,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            width: {
              xs: '100%',
              sm: '95%',
              md: 850,
              lg: 1000,
            },
          }}
        >
          <SectionSelector
            sections={sections}
            currentIndex={currentSectionIndex}
          />
          <Box
            sx={{
              p: 3,
              backgroundColor: '#F8F8F8',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              disableElevation
              onClick={handlePreviousSection}
              disabled={currentSectionIndex === 0 || currentSectionIndex === 9} // Disable on interview
            >
              <Translate expression='caregiverScreening.Main.Back'/>
            </Button>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForward />}
              disableElevation
              onClick={handleNextSection}
              disabled={
                disableNext ||
                currentSectionIndex === sections.length - 1 ||
                currentSectionIndex === 9 // Disable on confirm section
              }
            >
              <Translate expression='caregiverScreening.Main.Next'/>
            </Button>
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
};

CaregiverScreener.propTypes = {
};

export default React.memo(CaregiverScreener);
